import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, inject, Input, NgZone, OnDestroy, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RestService } from '../../../core/services/rest.service';
import { ToasterService } from '../../../core/services/toaster-service';
import { MessageEnum } from '../../../core/utils/message.enum';

export interface NavItem {
  id: string;
  text: string;
  level: number;
}

export interface PlatformTab {
  value: string;   // API key, e.g. JENKINS
  label: string;   // Display label, e.g. Jenkins
}

const PLATFORM_LABELS: Record<string, string> = {
  JENKINS: 'Jenkins',
  AZURE_DEVOPS: 'Azure DevOps',
  GITLAB_CI: 'GitLab CI',
  GITHUB_ACTIONS: 'GitHub Actions',
};

@Component({
  selector: 'app-cicd-config-info-modal',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule],
  templateUrl: './cicd-config-info-modal.component.html',
  styleUrl: './cicd-config-info-modal.component.scss',
})
export class CicdConfigInfoModalComponent implements OnInit, OnDestroy {
  @ViewChild('contentPane') contentPane!: ElementRef<HTMLDivElement>;

  readonly activeModal = inject(NgbActiveModal);
  private readonly restService = inject(RestService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly toaster = inject(ToasterService);
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() pipelineId: string = '';
  @Input() projectId: string = '';
  @Input() automationProjectId: string = '';

  platforms: PlatformTab[] = [];
  selectedPlatform: string = '';
  isLoadingPlatforms: boolean = true;
  isLoadingScript: boolean = false;
  loadError: boolean = false;

  navItems: NavItem[] = [];
  activeNavId: string = '';
  safeHtml: SafeHtml = '';

  script: string = '';
  fileName: string = '';
  isCopied: boolean = false;
  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.loadPlatforms();
  }

  ngOnDestroy(): void {
    if (this.copyResetTimer) {
      clearTimeout(this.copyResetTimer);
    }
  }

  // ── Data loading ─────────────────────────────────────────────────────────────
  private loadPlatforms(): void {
    this.isLoadingPlatforms = true;
    this.restService.getPipelinePlatforms(this.projectId, this.automationProjectId).subscribe({
      next: (res: any) => {
        const keys: string[] = res?.data ?? [];
        this.platforms = keys.map(key => ({
          value: key,
          label: PLATFORM_LABELS[key] ?? key,
        }));
        this.isLoadingPlatforms = false;
        if (this.platforms.length > 0) {
          this.selectPlatform(this.platforms[0].value);
        }
      },
      error: () => {
        this.isLoadingPlatforms = false;
        this.toaster.pop({ type: 'error', text: MessageEnum.FAILED_TO_LOAD_CICD_PLATFORMS });
      },
    });
  }

  selectPlatform(platform: string): void {
    if (this.selectedPlatform === platform && !this.isLoadingScript) return;
    this.selectedPlatform = platform;
    this.loadScript(platform);
  }

  private loadScript(platform: string): void {
    this.isLoadingScript = true;
    this.loadError = false;
    this.restService.getCiScriptByConfigId(
      this.projectId, this.automationProjectId, this.pipelineId, platform,
    ).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        this.script = data?.script ?? '';
        this.fileName = data?.fileName ?? 'ci-script.txt';
        this.buildView(data?.setupGuide ?? '');
        this.isLoadingScript = false;
      },
      error: () => {
        this.loadError = true;
        this.isLoadingScript = false;
        this.toaster.pop({ type: 'error', text: MessageEnum.FAILED_TO_LOAD_CICD_INTEGRATION_GUIDE });
      },
    });
  }

  private buildView(markdown: string): void {
    this.navItems = this.extractNavItems(markdown);
    this.activeNavId = this.navItems[0]?.id ?? '';
    const rawHtml = this.markdownToHtml(markdown);
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, rawHtml) ?? '';
    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(sanitized);
  }

  // ── Navigation ────────────────────────────────────────────────────────────────
  private extractNavItems(md: string): NavItem[] {
    const items: NavItem[] = [];
    const h2re = /^##\s+(.+)/;
    const h3re = /^###\s+(.+)/;
    for (const line of md.split('\n')) {
      const h3 = h3re.exec(line);
      const h2 = h2re.exec(line);
      if (h3) {
        //items.push({ id: this.headingId(h3[1]), text: h3[1].trim(), level: 3 });
      } else if (h2) {
        items.push({ id: this.headingId(h2[1]), text: h2[1].trim(), level: 2 });
      }
    }
    return items;
  }

  scrollToSection(item: NavItem): void {
    this.activeNavId = item.id;
    const el = this.contentPane?.nativeElement?.querySelector(`[data-nav-id="${item.id}"]`);
    if (el) {
      (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  platformLabel(p: string): string {
    return PLATFORM_LABELS[p] ?? p;
  }

  // ── Script actions ────────────────────────────────────────────────────────────
  copyScript(): void {
    navigator.clipboard.writeText(this.script)
      .then(() => this.ngZone.run(() => this.showCopiedState()))
      .catch(() => {
        this.toaster.pop({ type: 'error', text: 'Failed to copy script to clipboard.' });
      });
  }

  private showCopiedState(): void {
    this.isCopied = true;
    this.cdr.markForCheck();
    if (this.copyResetTimer) {
      clearTimeout(this.copyResetTimer);
    }
    this.copyResetTimer = setTimeout(() => this.ngZone.run(() => this.resetCopiedState()), 2000);
  }

  private resetCopiedState(): void {
    this.isCopied = false;
    this.copyResetTimer = null;
    this.cdr.markForCheck();
  }

  downloadScript(): void {
    const blob = new Blob([this.script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Markdown → HTML ───────────────────────────────────────────────────────────
  private markdownToHtml(md: string): string {
    const lines = md.split('\n');
    const html: string[] = [];
    let inCodeBlock = false;
    let codeLang = '';
    let codeBuffer: string[] = [];
    let inList: 'ul' | 'ol' | null = null;
    let inTable = false;
    let tableHeaderDone = false;
    let tableBuffer: string[][] = [];

    const closeList = (): void => {
      if (inList) { html.push(`</${inList}>`); inList = null; }
    };
    const closeTable = (): void => {
      if (!inTable) return;
      // Flush buffered table rows
      html.push('<table class="ci-md-table">');
      tableBuffer.forEach((cells, idx) => {
        if (idx === 0) {
          html.push('<thead><tr>' + cells.map(c => `<th>${c}</th>`).join('') + '</tr></thead>');
          html.push('<tbody>');
        } else {
          html.push('<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>');
        }
      });
      if (tableBuffer.length > 1) html.push('</tbody>');
      html.push('</table>');
      inTable = false;
      tableHeaderDone = false;
      tableBuffer = [];
    };
    const esc = (t: string): string =>
      t.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
    const inline = (t: string): string =>
      t
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>');
    const parseTableRow = (line: string): string[] =>
      line.split('|').slice(1, -1).map(cell => inline(esc(cell.trim())));
    const isTableSeparator = (line: string): boolean =>
      /^\|[\s|:-]+\|$/.test(line.trim());
    const isTableRow = (line: string): boolean =>
      line.trim().startsWith('|') && line.trim().endsWith('|');

    const h1re = /^#\s+(.+)/;
    const h2re = /^##\s+(.+)/;
    const h3re = /^###\s+(.+)/;
    const ulre = /^[-*]\s+(.+)/;
    const olre = /^\d+\.\s+(.+)/;

    for (const line of lines) {
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          inCodeBlock = false;
          const escaped = codeBuffer.map(l => esc(l)).join('\n');
          html.push(`<pre class="ci-code-block"><code class="lang-${codeLang || 'text'}">${escaped}</code></pre>`);
          codeBuffer = [];
        } else {
          closeList();
          closeTable();
          inCodeBlock = true;
          codeLang = line.slice(3).trim();
          codeBuffer = [];
        }
        continue;
      }
      if (inCodeBlock) { codeBuffer.push(line); continue; }

      // Table handling
      if (isTableRow(line)) {
        if (isTableSeparator(line)) {
          // Separator row — marks end of header, skip rendering it
          tableHeaderDone = true;
          continue;
        }
        if (!inTable) {
          closeList();
          inTable = true;
          tableHeaderDone = false;
          tableBuffer = [];
        }
        tableBuffer.push(parseTableRow(line));
        continue;
      } else if (inTable) {
        closeTable();
      }

      const h3m = h3re.exec(line);
      const h2m = h2re.exec(line);
      const h1m = h1re.exec(line);

      if (h3m) {
        closeList();
        const text = h3m[1].trim();
        html.push(`<h3 data-nav-id="${this.headingId(text)}">${inline(esc(text))}</h3>`);
        continue;
      }
      if (h2m) {
        closeList();
        const text = h2m[1].trim();
        html.push(`<h2 data-nav-id="${this.headingId(text)}">${inline(esc(text))}</h2>`);
        continue;
      }
      if (h1m) {
        closeList();
        html.push(`<h1>${inline(esc(h1m[1].trim()))}</h1>`);
        continue;
      }
      if (/^---+$/.test(line.trim())) {
        closeList();
        html.push('<hr />');
        continue;
      }

      const ulm = ulre.exec(line);
      if (ulm) {
        if (inList !== 'ul') { closeList(); html.push('<ul>'); inList = 'ul'; }
        html.push(`<li>${inline(esc(ulm[1]))}</li>`);
        continue;
      }
      const olm = olre.exec(line);
      if (olm) {
        if (inList !== 'ol') { closeList(); html.push('<ol>'); inList = 'ol'; }
        html.push(`<li>${inline(esc(olm[1]))}</li>`);
        continue;
      }
      if (line.trim() === '') { closeList(); continue; }

      closeList();
      html.push(`<p>${inline(esc(line.trim()))}</p>`);
    }

    closeList();
    closeTable();
    return html.join('\n');
  }

  private headingId(text: string): string {
    return 'nav-' + text.trim().replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  }
}
